import { ref } from 'vue';

const NEAR_END_THRESHOLD = 3;

/**
 * Offset pagination for a Collection-backed list that loads further pages as the user scrolls.
 *
 * @param {object} options
 * @param {function(number, number): Promise<object>} options.fetchPage Called as (offset, limit).
 * @param {function(Array): void} options.onPage Receives each loaded page's rows.
 * @param {function(Error, function): void} options.onError Receives the error and a retry callback.
 * @param {function(): Array} options.getItems Returns the currently rendered items.
 * @param {function(object): *} [options.getItemKey] Returns an item's identity key.
 * @param {function(): boolean} [options.enabled] Returns false to suppress all page requests.
 * @param {number} [options.limit] Page size.
 * @returns {object}
 */
export function usePaginatedCollection({
    fetchPage,
    onPage,
    onError,
    getItems,
    getItemKey = item => item?.id,
    enabled = () => true,
    limit = 40
}) {
    const offset = ref(0);
    const total = ref(null);
    const loadingMore = ref(false);
    const loadFailed = ref(false);

    function resetPagination() {
        offset.value = 0;
        total.value = null;
        loadFailed.value = false;
    }

    /**
     * Record the first page, so loadMore starts from the right cursor.
     *
     * @param {object} response
     */
    function setFirstPage(response) {
        offset.value = Array.isArray(response?.data) ? response.data.length : 0;
        total.value = response?.metadata?.total ?? null;
        loadFailed.value = false;
    }

    /**
     * Return the cursor to the end of the first page, keeping the known total. The rendered list
     * has been put back to its initial rows without a refetch, so the total is still valid.
     *
     * @param {number} count
     */
    function rewindToFirstPage(count) {
        offset.value = count;
        loadFailed.value = false;
    }

    async function loadMore() {
        if (!enabled() || loadingMore.value || loadFailed.value) {
            return;
        }

        if (total.value !== null && offset.value >= total.value) {
            return;
        }

        loadingMore.value = true;

        try {
            const response = await fetchPage(offset.value, limit);

            if (response && Array.isArray(response.data)) {
                // Advance by the rows returned, not by limit and not from the rendered array —
                // a search replaces that array, which runs the cursor backwards.
                offset.value += response.data.length;
                total.value = response.metadata?.total ?? total.value;

                // An empty page means the list is exhausted whatever the total claims. Leaving the
                // cursor short of it re-requests this same offset on every further intersection.
                if (!response.data.length) {
                    total.value = offset.value;
                }

                onPage(response.data);
            }
        } catch (error) {
            // Nothing else advances the cursor, so retrying unprompted repeats this page forever.
            // An abort is not a failure — ErrorService ignores it, and latching here would leave
            // pagination dead for a request the app itself cancelled.
            loadFailed.value = !error?.isAborted;
            onError(error, () => {
                loadFailed.value = false;
                return loadMore();
            });
        } finally {
            loadingMore.value = false;
        }
    }

    /**
     * Vuetify's v-intersect binds on `inserted` and has no update hook, so the observer's
     * handler holds the item object from the render that inserted the row. A list rebuilt
     * since then holds equal items under different identities, so fall back to the key.
     *
     * @param {Array} items
     * @param {object} item
     * @returns {number}
     */
    function indexOfItem(items, item) {
        const index = items.indexOf(item);

        if (index !== -1) {
            return index;
        }

        const key = getItemKey(item);

        return key === null || key === undefined ? -1 : items.findIndex(other => getItemKey(other) === key);
    }

    function onIntersect(item) {
        if (total.value === null || total.value <= limit) {
            return;
        }

        const items = getItems();
        const index = indexOfItem(items, item);

        if (index < 0 || index < items.length - NEAR_END_THRESHOLD) {
            return;
        }

        loadMore();
    }

    return {
        offset,
        total,
        limit,
        loadingMore,
        loadFailed,
        resetPagination,
        setFirstPage,
        rewindToFirstPage,
        loadMore,
        onIntersect
    };
}
